import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom';
import { fetchOneServiceType, fetchServiceImage } from '../../services/Service';
import { Service, ServiceAddOn } from '../../types/Service';
import { getAssetById } from '../../services/Asset';
import { Button, Checkbox, Form, Icon, Message } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import { createOrder, updateOrder } from '../../services/OrderStatus';
import { fetchOrderImageUploadUrl } from '../../services/OrderStatus';
import _ from 'lodash';
import sendToast from '../../../utils/Toast';
import { uploadToSignedUrl } from '../../../services/imageUpload';
import { Document } from '../../types/Document';
import { resolveServiceDocuments } from '../../services/Documents';
import { getAttachedFileLink } from '../../utils/document';
import useResidentOrderCardEligibility from '../../hooks/useResidentOrderCardEligibility';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import { isPaymentRequiredForService } from '../../utils/paymentStatus';
import { CardGateReason, getCardGateActionLabel, getCardGateMessage, resolveCardGateReason } from '../../utils/orderCardGate';
import ChargeBreakdown from '../ChargeBreakdown';
import useRequestChargePreview from '../../hooks/useRequestChargePreview';

interface StepThreeProps {
    FacilityId: string;
    selectedServiceId: string;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
    setStepForm: React.Dispatch<React.SetStateAction<number>>;
    selectedResidentId: string;
    selectedServiceCategoryName: string;
    setSelectedServiceId: React.Dispatch<React.SetStateAction<string>>;
    setSelectedServiceCategoryName: React.Dispatch<React.SetStateAction<string>>;
    refreshHandler?: () => void;
    source?: string;
    isFetching?: boolean;
    facilityPaymentsEnabled?: boolean;
}

const StepThree: React.FC<StepThreeProps> = ({ FacilityId, selectedServiceId, setIsFetching, setStepForm, selectedResidentId, selectedServiceCategoryName, setSelectedServiceId, setSelectedServiceCategoryName, refreshHandler, source, isFetching = false, facilityPaymentsEnabled = false }) => {
    const history = useHistory();
    const [selectedService, setSelectedService] = useState<Service>({} as Service);
    const {
        loading: cardEligibilityLoading,
        hasDefaultCard,
        hasAnyCard,
        defaultCardExpired,
        lookupFailed,
        refresh: refreshCardEligibility,
    } = useResidentOrderCardEligibility({
        residentId: selectedResidentId,
        facilityId: FacilityId,
        enabled: facilityPaymentsEnabled,
    });
    // If the facility settings could not load, payments are treated as on and the gate
    // shows could-not-verify until a retry succeeds.
    const { facilityLookupFailed, retry: retryFacilityPayments } = useNeighborsPaymentAccess();
    const [serviceImage, setServiceImages] = useState<string>("");
    const [assetName, setAssetName] = useState<string>("");
    const [assetUrl, setAssetUrl] = useState<string>("");
    const [serviceDocs, setServiceDocs] = useState<Document[]>([]);
    const [loadingDocs, setLoadingDocs] = useState<boolean>(true);
    const [serviceAddOns, setServiceAddOns] = useState<ServiceAddOn[]>([]);
    const [externalCost, setExternalCost] = useState<number>(0);
    const [orderName, setOrderName] = useState<string>('');
    const [selectedServiceAddOns, setSelectedServiceAddOns] = useState<ServiceAddOn[]>([]);
    const [description, setDescription] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [nowRequest, setNowRequest] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [currentImage, setCurrentImage] = useState<string>('');
    const [imageLink, setImageLink] = useState<string>('');
    // Blocks a second submit (Enter or a fast double click) before isFetching re-renders
    // the Send button as disabled.
    const isSubmittingRef = useRef(false);

    useEffect(() => {
        const requiredAddons = serviceAddOns.filter((addon) => addon.required);
        const selectedAddons = requiredAddons.map((addon) => ({
            id: addon.id,
            itemName: addon.itemName,
            itemCost: addon.itemCost,
            required: addon.required,
        }));
        setSelectedServiceAddOns(selectedAddons);
    }, [serviceAddOns])

    useEffect(() => {
        const fetchService = async () => {
            setIsFetching(true);
            try {
                if (selectedResidentId && selectedServiceCategoryName && selectedServiceId) {
                    const serviceRes = await fetchOneServiceType(selectedServiceId, FacilityId, selectedResidentId);
                    const fetchedService = serviceRes?.Result?.Content?.Service;
                    if (!fetchedService) {
                        throw new Error('Service is not available for this resident');
                    }

                    if (fetchedService.image) {
                        const serviceImageUrl = await fetchServiceImage({
                            serviceId: fetchedService._id,
                            Facility: FacilityId,
                            fileName: fetchedService.image,
                        });
                        setServiceImages(serviceImageUrl);
                    }

                    if (fetchedService.Asset) {
                        try {
                            const assetRes = await getAssetById({
                                Facility: FacilityId,
                                AssetId: fetchedService.Asset,
                            });
                            if (assetRes && assetRes.response) {
                                setAssetName(assetRes.response.AssetName || '');
                                setAssetUrl(assetRes.response.URL || assetRes.response.SignedUrl || '');
                            } else {
                                setAssetName('');
                                setAssetUrl('');
                            }
                        } catch (assetError) {
                            setAssetName('');
                            setAssetUrl('');
                            sendToast('error', assetError instanceof Error ? assetError.message : 'An error occurred while fetching asset details');
                        }
                    } else {
                        setAssetName('');
                        setAssetUrl('');
                    }

                    fetchedService.ServiceAddOns && setServiceAddOns(fetchedService.ServiceAddOns);
                    setExternalCost((fetchedService && fetchedService.defaults && fetchedService.defaults.externalCost) || 0);
                    setSelectedService(fetchedService);
                }
            }
            catch (error) {
                setSelectedService({} as Service);
                setServiceAddOns([]);
                setExternalCost(0);
                setServiceImages('');
                setAssetName('');
                setAssetUrl('');
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching services');
            }
            finally {
                setIsFetching(false);
            }
        };
        fetchService();
    }, [selectedServiceId, selectedResidentId, selectedServiceCategoryName, FacilityId]);

    useEffect(() => {
        const fetchServiceDocs = async () => {
            try {
                setLoadingDocs(true);
                const docs = await resolveServiceDocuments(FacilityId, selectedService);
                setServiceDocs(docs);
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching service document');
                setServiceDocs([]);
            }
            finally {
                setLoadingDocs(false);
            }
        };

        fetchServiceDocs();
    }, [selectedService?.DocIds, FacilityId]);


    const handleServiceAddons = (id: string, value: boolean) => {
        let updatedAddOns = _.cloneDeep(selectedServiceAddOns);
        if (value) {
            serviceAddOns.forEach(((addon) => {
                if (addon.id === id) {
                    updatedAddOns.push(addon);
                }
            }));
        } else {
            updatedAddOns = updatedAddOns.filter((addon: ServiceAddOn) => addon.id !== id);
        }
        setSelectedServiceAddOns(updatedAddOns);
    };

    const handleNowRequest = () => {
        if (nowRequest) {
            setNowRequest(false);
        } else {
            setStartDate('');
            setNowRequest(true);
        }
    };

    const totalCostForOrder: number =
        selectedServiceAddOns
            .reduce((acc, addon) => acc + Number(addon.itemCost), 0) +
        Number(externalCost);

    const paymentRequired = facilityPaymentsEnabled && isPaymentRequiredForService(selectedService);
    const cardGateReason: CardGateReason = paymentRequired
        ? resolveCardGateReason({ loading: cardEligibilityLoading, lookupFailed: lookupFailed || facilityLookupFailed, hasDefaultCard, hasAnyCard, defaultCardExpired })
        : null;

    const { shownChargePreview, chargePreviewLoading, resolvePaymentConsent } = useRequestChargePreview({
        paymentRequired,
        facilityId: FacilityId,
        residentId: selectedResidentId,
        serviceId: selectedService && selectedService._id ? String(selectedService._id) : '',
        addOnIds: selectedServiceAddOns.map((addOn) => String(addOn.id)),
    });

    const handleRetryCardEligibility = async () => {
        if (facilityLookupFailed) retryFacilityPayments();
        await refreshCardEligibility();
    };

    const handleSubmit = async () => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        try {
            setIsFetching(true);
            if (!selectedService || !selectedService._id) {
                throw new Error('Please select a valid service');
            }
            if (cardGateReason) {
                throw new Error(getCardGateMessage(cardGateReason));
            }
            if (!nowRequest && !moment(startDate).isValid()) {
                throw new Error('Please select a valid end date')
            }
            if (!selectedResidentId || !selectedServiceCategoryName || !selectedServiceId || !orderName.trim() || !description.trim() || !startDate) {

                const errors: string[] = [];

                if (!selectedResidentId) {
                    errors.push("Please select a resident");
                }
                if (!selectedServiceCategoryName) {
                    errors.push("Please select a service category");
                }
                if (!selectedServiceId) {
                    errors.push("Please select a service");
                }
                if (!orderName.trim()) {
                    errors.push("Order name cannot be empty");
                }
                if (!startDate && !nowRequest) {
                    errors.push("Please select start date");
                }
                if (errors.length > 0) {
                    const errorMessage = errors.join('\n,');
                    throw new Error(errorMessage);
                }
            }
            const facilityId = FacilityId;
            const startDateMomentObj = momentTZ(startDate);
            const endDate = startDateMomentObj.clone().add(15, 'minutes'); // set end date to 15 minutes after the start date by default
            const paymentConsent = await resolvePaymentConsent('neighbors_create_order');
            const requestObject = {
                Category: selectedServiceCategoryName,
                ResidentId: selectedResidentId,
                OrderName: orderName,
                Description: description,
                ...(!nowRequest && {
                    ScheduledTime: {
                        startDate: moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
                        endDate: moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
                    },
                }),
                ...(nowRequest && { RequestedTime: moment().valueOf() }),
                Facility: facilityId,
                RequestChannel: "Web",
                ServiceAddOns: selectedServiceAddOns,
            };
            const Data = {
                ...requestObject,
                OriginalServiceId: String(selectedService._id),
                ...(paymentConsent && { payment: { consent: paymentConsent } }),
            }
            const createdRequestId = await createOrder(FacilityId, Data);
            const createdContent = createdRequestId?.Result?.Content;
            if (!createdContent) {
                throw new Error('An error occurred while creating request')
            }
            const stringifiedRequestId = String(createdContent);
            // The request (and its charge consent) exists now, so an image problem must not look
            // like a failed order: warn, then reset the form as usual.
            let imageAttachFailed = false;
            try {
                if (selectedImage) {
                    await uploadImageFile(stringifiedRequestId);
                    const updateRes = await updateOrder(FacilityId, {
                        _id: stringifiedRequestId,
                        ImageName: selectedImage.name,
                    });
                    if (!updateRes.Result.Content.updated && updateRes.Result.Content.message) { // backend will return updated as false with a message as to why updated is false
                        throw new Error(updateRes.Result.Content.message);
                    }
                } else if (selectedService && selectedService.image && serviceImage) {
                    await copyServiceImageToOrder(stringifiedRequestId);
                    const updateRes = await updateOrder(FacilityId, {
                        _id: stringifiedRequestId,
                        ImageName: selectedService.image,
                    });
                    if (!updateRes.Result.Content.updated && updateRes.Result.Content.message) { // backend will return updated as false with a message as to why updated is false
                        throw new Error(updateRes.Result.Content.message);
                    }
                }
            } catch (imageError) {
                imageAttachFailed = true;
                console.error('Order image attach failed', imageError);
            }
            if (imageAttachFailed) {
                sendToast('warn', 'Request created, but the image could not be attached.');
            } else {
                sendToast('success', 'Request created successfully');
            }
            refreshHandler && refreshHandler();
            setStepForm(1);
            resetState();
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'An error occurred while creating request');
        } finally {
            setIsFetching(false);
            isSubmittingRef.current = false;
        }

    }

    const uploadImageFile = async (orderId: string) => {
        if (!selectedImage) return;
        const putUrl = await fetchOrderImageUploadUrl(FacilityId, orderId, selectedImage.name);
        await uploadToSignedUrl(selectedImage, putUrl.Result.Content);
    };

    const copyServiceImageToOrder = async (orderId: string) => {
        if (!selectedService || !selectedService.image || !serviceImage) return;

        const response = await fetch(serviceImage, { cache: 'no-store' });
        if (!response.ok) {
            throw new Error('Failed to download service image for copy');
        }
        const blob = await response.blob();

        const putUrl = await fetchOrderImageUploadUrl(FacilityId, orderId, selectedService.image);
        await uploadToSignedUrl(blob, putUrl.Result.Content);
    };


    const handleCancel = () => {
        setStepForm(1);
        resetState();
    }

    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const selectedFile = event.target.files[0];
            if (selectedFile) {
                setSelectedImage(selectedFile);
                setCurrentImage('');
                setImageLink('');
            }
        }
    };

    const resetState = () => {
        setSelectedServiceId('');
        setSelectedServiceAddOns([]);
        setOrderName('');
        setDescription('');
        setSelectedServiceCategoryName('');
        setSelectedImage(null);
        setCurrentImage('');
        setImageLink('');
    }

    return (
        <>
            <Form onSubmit={handleSubmit} style={{ backgroundColor: '#fff', padding: '5px' }}>
                <div className="stepThreeLayout">

                    {/* LEFT COLUMN: heading + all form fields + buttons */}
                    <div className="stepThreeLeft">
                        <h2 className="stepThreeCategory">{selectedServiceCategoryName}</h2>
                        <h2 className="stepThreeServiceName">{selectedService?.name}</h2>
                        {selectedService?.shortDescription && (
                            <p className="stepThreeDescription">{selectedService.shortDescription}</p>
                        )}

                        {assetName && (
                            <p className="stepThreeMeta">
                                Asset:{' '}
                                {assetUrl ? (
                                    <a
                                        href={assetUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#0066cc', textDecoration: 'underline' }}
                                    >
                                        <span style={{ fontWeight: 'bold' }}>{assetName}</span>
                                    </a>
                                ) : (
                                    assetName
                                )}
                            </p>
                        )}
                        {(loadingDocs || serviceDocs.length > 0) && (
                            <p className="stepThreeMeta">
                                Attached Docs:{' '}
                                {loadingDocs ? (
                                    <Icon name="spinner" loading />
                                ) : (
                                    <>
                                        {serviceDocs.map((doc) => {
                                            const href = getAttachedFileLink(doc);
                                            if (!href) return null;
                                            return (
                                                <a
                                                    key={doc._id || doc.Name}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: '#0066cc', 
                                                        textDecoration: 'underline', 
                                                        marginLeft: '6px',
                                                    }}
                                                >
                                                    <span style={{ fontWeight: 'bold' }}>{doc.Name}</span>
                                                </a>
                                            );
                                        })}
                                    </>
                                )}
                            </p>
                        )}

                        {cardGateReason && (
                            <Message warning className="orderCardGateNotice">
                                <Message.Header>Card Required For Payable Order</Message.Header>
                                <p>{getCardGateMessage(cardGateReason)}</p>
                                <div className="orderCardGateActions">
                                    <Button type="button" size="small" primary onClick={() => history.push('/payments')}>
                                        {getCardGateActionLabel(cardGateReason)}
                                    </Button>
                                    <Button type="button" size="small" onClick={handleRetryCardEligibility} loading={cardEligibilityLoading} disabled={cardEligibilityLoading}>
                                        Retry
                                    </Button>
                                </div>
                            </Message>
                        )}

                        <Form.Field style={{ marginTop: '12px' }}>
                            <Form.Input
                                required={true}
                                value={orderName || ''}
                                placeholder="Order name"
                                onChange={(e) => setOrderName(e.target.value)}
                            />
                        </Form.Field>

                        <Form.Field>
                            <Form.TextArea
                                value={description}
                                onChange={(e) => setDescription(e.currentTarget.value)}
                                placeholder="Order description"
                            />
                        </Form.Field>

                        <Form.Field>
                            <div className="stepThreeTimeRow">
                            <DatePicker
                                customInput={
                                    <div
                                        style={{
                                            position: 'relative',
                                            boxShadow: '0px 1px 2px 0 rgba(34, 36, 38, 0.15)',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Icon
                                            name="calendar alternate outline"
                                            style={{
                                                position: 'absolute',
                                                top: '45%',
                                                left: '10px',
                                                transform: 'translateY(-50%)',
                                            }}
                                        />
                                        <input
                                            disabled={nowRequest}
                                            style={{ paddingLeft: '30px', height: "37px", border: "none" }}
                                            placeholder="Schedule start *"
                                            value={startDate ? moment(startDate).format('M.D.Y h:mm A') : ''}
                                        />
                                    </div>
                                }
                                minDate={new Date()} // Set the minimum to today
                                disabled={nowRequest}
                                dateFormat="M.d.Y h:mm aa"
                                showTimeSelect
                                timeIntervals={15}
                                selected={startDate ? Date.parse(startDate || new Date().toISOString()) : null}
                                onChange={(date: Date) => {
                                    if (!date) return;
                                    if (!nowRequest) {
                                        const dateTime = moment(date).format('YYYY-MM-DDTHH:mm:ss');
                                        setStartDate(dateTime);
                                    } else {
                                        setStartDate('');
                                    }
                                }}
                            />
                                <div
                                    className={`stepThreeNowBtn${nowRequest ? ' active' : ''}`}
                                    onClick={() => handleNowRequest()}
                                >
                                    <Icon name="calendar alternate outline" />
                                    <span>Now</span>
                                </div>
                            </div>
                        </Form.Field>

                        {/* hidden image upload - keeps functionality */}
                        <input
                            id="imageInput"
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                        />

                        <Form.Field style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <Button
                                fluid
                                style={{ fontWeight: '600', fontSize: '18px' }}
                                type="submit"
                                disabled={isFetching || Boolean(cardGateReason)}
                            >
                                Send
                            </Button>
                            <Button
                                fluid
                                style={{ fontWeight: '600', fontSize: '18px' }}
                                type="button"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                        </Form.Field>
                    </div>

                    {/* RIGHT COLUMN: image picker + add-ons + total */}
                    <div className="stepThreeRight">
                        <div className="stepThreeImagePicker">
                            <label htmlFor="imageInput" style={{ cursor: 'pointer', display: 'block', width: '100%' }}>
                                {selectedImage ? (
                                    <img
                                        src={URL.createObjectURL(selectedImage)}
                                        alt="Selected"
                                        className="stepThreeServiceImage"
                                    />
                                ) : currentImage && imageLink ? (
                                    <img
                                        src={imageLink}
                                        alt="Existing"
                                        className="stepThreeServiceImage"
                                    />
                                ) : serviceImage ? (
                                    <img
                                        src={serviceImage}
                                        alt={selectedService?.name}
                                        className="stepThreeServiceImage"
                                    />
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                        <Icon name="image" size="huge" />
                                    </div>
                                )}
                            </label>
                        </div>

                        {(serviceAddOns.length > 0 || (externalCost && externalCost > 0)) ? (
                            <div className="stepThreeAddOns">
                                {serviceAddOns.map((item: any) => {
                                    const isAddonPresentInOrder = !!selectedServiceAddOns.find((addon) => addon.id === item.id);
                                    return (
                                        <div key={item.id} className="stepThreeAddonRow">
                                            <label>{item.itemName}</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Checkbox
                                                    disabled={item.required}
                                                    checked={item.required || isAddonPresentInOrder}
                                                    onChange={(event: React.FormEvent<HTMLInputElement>, { checked }) =>
                                                        handleServiceAddons(item.id, checked as boolean)
                                                    }
                                                />
                                                {item.itemCost > 0 && (
                                                    <span><b>$</b>{item.itemCost}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {externalCost && externalCost > 0 ? (
                                    <div className="stepThreeAddonRow">
                                        <label>External Cost</label>
                                        <span><b>$</b>{externalCost}</span>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {paymentRequired && shownChargePreview && shownChargePreview.breakdown ? (
                            <div className="stepThreeTotal">
                                <ChargeBreakdown
                                    breakdown={shownChargePreview.breakdown}
                                    note={shownChargePreview.capApplied ? 'Capped at the maximum single charge.' : undefined}
                                />
                            </div>
                        ) : totalCostForOrder > 0 && (
                            <div className="stepThreeTotal">
                                <span>{paymentRequired ? 'Subtotal' : 'Total'}:&nbsp;&nbsp;<b>${totalCostForOrder}</b></span>
                                {paymentRequired && (chargePreviewLoading ? ' · calculating tax…' : ' + tax')}
                            </div>
                        )}
                    </div>

                </div>
            </Form>
        </>
    )
}

export default StepThree
